import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Modal, Badge } from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';

const toLimitInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const parseLimitInput = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// Límites de frecuencia/concurrencia por plan (billing.Plans, RateLimitFilter). Vacío = sin límite.
const RATE_LIMIT_FIELDS = [
  { key: 'rateLimitPerMinuteAccount', label: 'Peticiones/min por cuenta' },
  { key: 'rateLimitPerMinuteRfc', label: 'Peticiones/min por RFC' },
  { key: 'maxConcurrentAccount', label: 'Simultáneas por cuenta' },
  { key: 'maxConcurrentRfc', label: 'Simultáneas por RFC' },
];

const MODE_INFO = {
  Enforce: { bg: 'danger', label: 'Enforce', text: 'Bloquea: responde 429 al exceder un límite.' },
  Observe: {
    bg: 'info',
    label: 'Observe',
    text: 'No bloquea: registra en TelemetryLogs (reason = RateLimitObserved) las peticiones que se habrían rechazado.',
  },
};

const formatLimit = (value) => (value === null || value === undefined ? 'sin límite' : value);

/**
 * Configuración global vigente de RateLimit (appsettings / App Settings del ambiente). Solo lectura:
 * se cambia en la configuración del App Service (p. ej. RateLimit__Mode), no desde el panel.
 */
const RateLimitConfigPanel = ({ config, error, onRefresh }) => {
  if (error) {
    return <Alert variant="warning" className="py-2 small">No se pudo leer la configuración de RateLimit: {error}</Alert>;
  }
  if (!config) {
    return <div className="small text-muted mb-2"><Spinner animation="border" size="sm" /> Cargando configuración…</div>;
  }

  const mode = MODE_INFO[config.mode] ?? MODE_INFO.Observe;
  const modeTypo = config.enabled && config.configuredMode && config.configuredMode !== config.mode;

  return (
    <div className="mb-3 p-2 rounded bg-light small">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        <strong>Configuración actual del ambiente:</strong>
        <Badge bg={config.enabled ? 'success' : 'secondary'}>{config.enabled ? 'Activado' : 'Desactivado'}</Badge>
        {config.enabled && <Badge bg={mode.bg}>{mode.label}</Badge>}
        <Button variant="link" size="sm" className="p-0 ms-auto" onClick={onRefresh}>Actualizar</Button>
      </div>
      <div className="text-muted mb-1">
        {config.enabled
          ? mode.text
          : 'El filtro está apagado (RateLimit:Enabled = false): los límites de los planes no se aplican ni se registran.'}
      </div>
      {modeTypo && (
        <div className="text-danger mb-1">
          RateLimit:Mode = "{config.configuredMode}" no es un valor válido; se comporta como Observe.
        </div>
      )}
      <div className="text-muted">
        Cuentas sin suscripción activa: {formatLimit(config.defaultPerMinuteAccount)}/min por cuenta ·{' '}
        {formatLimit(config.defaultPerMinuteRfc)}/min por RFC · {formatLimit(config.defaultMaxConcurrentAccount)} simultáneas
        por cuenta · {formatLimit(config.defaultMaxConcurrentRfc)} por RFC.
        {' '}Cupo huérfano expira en {config.leaseTtlSeconds} s · Retry-After por concurrencia {config.concurrencyRetryAfterSeconds} s ·
        timeout SQL {config.commandTimeoutSeconds} s (fail-open).
      </div>
      <div className="text-muted fst-italic mt-1">Se cambia en la configuración del ambiente (App Settings, p. ej. RateLimit__Mode), no aquí.</div>
    </div>
  );
};

const AdminFeatureConfig = () => {
  const { getAuthHeaders } = useAuthHeaders();

  // Rate limits del plan seleccionado
  const [rateLimitInputs, setRateLimitInputs] = useState({});
  const [rateLimitSaving, setRateLimitSaving] = useState(false);
  const [rateLimitFeedback, setRateLimitFeedback] = useState(null);

  // Configuración global vigente (sección RateLimit de appsettings / App Settings), solo lectura
  const [rateLimitConfig, setRateLimitConfig] = useState(null);
  const [rateLimitConfigError, setRateLimitConfigError] = useState(null);

  // Plans
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  // Plan x Feature matrix for the selected plan
  const [matrix, setMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);
  const [rowState, setRowState] = useState({}); // featureTypeId -> { saving, feedback }

  // Feature types (catalog) management
  const [featureTypes, setFeatureTypes] = useState([]);
  const [featureTypesLoading, setFeatureTypesLoading] = useState(true);
  const [featureTypesError, setFeatureTypesError] = useState(null);

  const [showNewFeatureModal, setShowNewFeatureModal] = useState(false);
  const [newFeatureCode, setNewFeatureCode] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [creatingFeature, setCreatingFeature] = useState(false);
  const [createFeatureFeedback, setCreateFeatureFeedback] = useState(null);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/plans`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setPlans(data);
      if (data.length > 0) {
        setSelectedPlanId((prev) => prev || String(data[0].id));
      }
    } catch (err) {
      setPlansError(err.message);
    } finally {
      setPlansLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchFeatureTypes = useCallback(async () => {
    setFeatureTypesLoading(true);
    setFeatureTypesError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/feature-types`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setFeatureTypes(data);
    } catch (err) {
      setFeatureTypesError(err.message);
    } finally {
      setFeatureTypesLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchMatrix = useCallback(async (planId) => {
    if (!planId) return;
    setMatrixLoading(true);
    setMatrixError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/plans/${planId}/features`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setMatrix(data.map((item) => ({
        ...item,
        dailyLimitInput: toLimitInputValue(item.dailyLimit),
        monthlyLimitInput: toLimitInputValue(item.monthlyLimit),
      })));
      setRowState({});
    } catch (err) {
      setMatrixError(err.message);
    } finally {
      setMatrixLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  useEffect(() => { fetchFeatureTypes(); }, [fetchFeatureTypes]);
  useEffect(() => { fetchMatrix(selectedPlanId); }, [selectedPlanId, fetchMatrix]);

  const fetchRateLimitConfig = useCallback(async () => {
    setRateLimitConfigError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/rate-limit-config`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      setRateLimitConfig(await response.json());
    } catch (err) {
      setRateLimitConfigError(err.message);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchRateLimitConfig(); }, [fetchRateLimitConfig]);

  const selectedPlan = plans.find((p) => String(p.id) === String(selectedPlanId));

  // Sincroniza los campos con los valores guardados del plan. Depende de `plans` porque al guardar se
  // actualiza el plan en memoria; por eso el mensaje de guardado NO se limpia aquí (lo borraba al instante).
  useEffect(() => {
    const inputs = {};
    RATE_LIMIT_FIELDS.forEach(({ key }) => { inputs[key] = toLimitInputValue(selectedPlan?.[key]); });
    setRateLimitInputs(inputs);
  }, [selectedPlan]);

  // El mensaje solo se limpia al cambiar de plan.
  useEffect(() => { setRateLimitFeedback(null); }, [selectedPlanId]);

  const rateLimitDirty = RATE_LIMIT_FIELDS.some(({ key }) =>
    parseLimitInput(rateLimitInputs[key]) !== (selectedPlan?.[key] ?? null));

  const handleRateLimitInputChange = (key, value) => {
    setRateLimitInputs((prev) => ({ ...prev, [key]: value }));
    // Un "Guardado" ya no describe lo que hay en pantalla en cuanto se edita algo.
    setRateLimitFeedback((prev) => (prev?.ok ? null : prev));
  };

  const handleSaveRateLimits = async () => {
    setRateLimitSaving(true);
    setRateLimitFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const body = {};
      RATE_LIMIT_FIELDS.forEach(({ key }) => { body[key] = parseLimitInput(rateLimitInputs[key]); });
      const response = await fetch(`${API_BASE}/admin/billing/plans/${selectedPlanId}/rate-limits`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setRateLimitFeedback({ ok: false, message: data?.message || `Error ${response.status}` });
        return;
      }
      // Se toman los valores que devolvió el servidor, no los enviados: así lo que se ve es lo que quedó en BD.
      const saved = {};
      RATE_LIMIT_FIELDS.forEach(({ key }) => { saved[key] = data?.[key] ?? null; });
      setPlans((prev) => prev.map((p) => (String(p.id) === String(selectedPlanId) ? { ...p, ...saved } : p)));
      const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setRateLimitFeedback({
        ok: true,
        message: `Límites de ${selectedPlan?.name ?? 'el plan'} guardados a las ${hora}. Aplican desde la siguiente petición.`,
      });
    } catch (err) {
      setRateLimitFeedback({ ok: false, message: err.message });
    } finally {
      setRateLimitSaving(false);
    }
  };

  const updateMatrixRow = (featureTypeId, changes) => {
    setMatrix((prev) => prev.map((row) =>
      row.featureTypeId === featureTypeId ? { ...row, ...changes } : row
    ));
  };

  const handleSaveRow = async (row) => {
    setRowState((prev) => ({ ...prev, [row.featureTypeId]: { saving: true, feedback: null } }));
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/billing/plans/${selectedPlanId}/features/${row.featureTypeId}`,
        {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isEnabled: row.isEnabled,
            dailyLimit: parseLimitInput(row.dailyLimitInput),
            monthlyLimit: parseLimitInput(row.monthlyLimitInput),
          }),
        }
      );
      const data = await response.json().catch(() => null);
      setRowState((prev) => ({
        ...prev,
        [row.featureTypeId]: {
          saving: false,
          feedback: { ok: response.ok, message: response.ok ? 'Guardado.' : (data?.message || `Error ${response.status}`) },
        },
      }));
    } catch (err) {
      setRowState((prev) => ({
        ...prev,
        [row.featureTypeId]: { saving: false, feedback: { ok: false, message: err.message } },
      }));
    }
  };

  const handleCreateFeature = async () => {
    setCreatingFeature(true);
    setCreateFeatureFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/feature-types`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newFeatureCode.trim(), description: newFeatureDescription.trim() || null }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setCreateFeatureFeedback({ ok: false, message: data?.message || `Error ${response.status}` });
        return;
      }
      setCreateFeatureFeedback({ ok: true, message: 'Funcionalidad creada.' });
      setNewFeatureCode('');
      setNewFeatureDescription('');
      await fetchFeatureTypes();
      await fetchMatrix(selectedPlanId);
    } catch (err) {
      setCreateFeatureFeedback({ ok: false, message: err.message });
    } finally {
      setCreatingFeature(false);
    }
  };

  const handleToggleFeatureActive = async (featureType) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${API_BASE}/admin/billing/feature-types/${featureType.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: featureType.description, isActive: !featureType.isActive }),
      });
      await fetchFeatureTypes();
      await fetchMatrix(selectedPlanId);
    } catch {
      // silently ignore; list will just not reflect the change
    }
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Admin — Features y Límites por Plan</h2>
          <p className="page-subtitle text-muted">
            Habilita o deshabilita funcionalidades por plan y ajusta sus límites diarios/mensuales y de
            frecuencia/concurrencia.
            Para dar de alta un Plan nuevo (con su Price en Stripe) sigue siendo manual.
          </p>
        </div>
      </div>

      {plansError && <Alert variant="danger">Error al cargar planes: {plansError}</Alert>}

      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={4}>
            <Form.Label className="small mb-1">Plan</Form.Label>
            <Form.Select
              size="sm"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={plansLoading || plans.length === 0}
            >
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.code}){!p.active ? ' — inactivo' : ''}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      {selectedPlanId && (
        <div className="mb-4 p-3 border rounded">
          <div className="d-flex align-items-center gap-2 mb-1">
            <h6 className="mb-0">Límites de frecuencia y concurrencia</h6>
            {rateLimitDirty && <Badge bg="warning" text="dark">Cambios sin guardar</Badge>}
          </div>
          <p className="small text-muted mb-2">
            Aplican a Consultar y SatWebService (respuesta 429). El límite por RFC es global: lo comparten
            todas las cuentas que operan ese RFC. Vacío = sin límite.
          </p>

          <RateLimitConfigPanel config={rateLimitConfig} error={rateLimitConfigError} onRefresh={fetchRateLimitConfig} />

          <Row className="g-2 align-items-end">
            {RATE_LIMIT_FIELDS.map(({ key, label }) => {
              const savedValue = selectedPlan?.[key] ?? null;
              const changed = parseLimitInput(rateLimitInputs[key]) !== savedValue;
              return (
                <Col xs={6} md={2} key={key}>
                  <Form.Label className="small mb-1">{label}</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    placeholder="Ilimitado"
                    className={changed ? 'border-warning' : undefined}
                    value={rateLimitInputs[key] ?? ''}
                    onChange={(e) => handleRateLimitInputChange(key, e.target.value)}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Guardado: {savedValue ?? 'sin límite'}
                  </Form.Text>
                </Col>
              );
            })}
            <Col xs={12} md={4} className="pb-3">
              <Button
                size="sm"
                variant={rateLimitDirty ? 'primary' : 'outline-secondary'}
                onClick={handleSaveRateLimits}
                disabled={rateLimitSaving || !rateLimitDirty}
              >
                {rateLimitSaving ? <Spinner animation="border" size="sm" /> : 'Guardar límites'}
              </Button>
            </Col>
          </Row>

          {rateLimitFeedback && (
            <Alert
              variant={rateLimitFeedback.ok ? 'success' : 'danger'}
              className="py-2 mt-2 mb-0 small"
              dismissible
              onClose={() => setRateLimitFeedback(null)}
            >
              {rateLimitFeedback.message}
            </Alert>
          )}
        </div>
      )}

      {matrixError &&<Alert variant="danger">Error al cargar features: {matrixError}</Alert>}

      {matrixLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <div className="table-responsive mb-4">
          <Table hover size="sm" className="mb-2">
            <thead className="table-light">
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th>Habilitado</th>
                <th>Límite diario</th>
                <th>Límite mensual</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => {
                const state = rowState[row.featureTypeId] || {};
                return (
                  <tr key={row.featureTypeId}>
                    <td className="text-monospace">{row.featureCode}</td>
                    <td className="text-muted">{row.featureDescription || '—'}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={row.isEnabled}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { isEnabled: e.target.checked })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        placeholder="Ilimitado"
                        style={{ width: 110 }}
                        value={row.dailyLimitInput}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { dailyLimitInput: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        placeholder="Ilimitado"
                        style={{ width: 110 }}
                        value={row.monthlyLimitInput}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { monthlyLimitInput: e.target.value })}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleSaveRow(row)}
                          disabled={state.saving}
                        >
                          {state.saving ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                        {state.feedback && (
                          <small className={state.feedback.ok ? 'text-success' : 'text-danger'}>
                            {state.feedback.message}
                          </small>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {matrix.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No hay funcionalidades activas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <div className="page-header mb-3 mt-5">
        <div>
          <h4 className="page-title">Funcionalidades (FeatureTypes)</h4>
        </div>
        <Button size="sm" variant="primary" onClick={() => { setShowNewFeatureModal(true); setCreateFeatureFeedback(null); }}>
          + Nueva funcionalidad
        </Button>
      </div>

      {featureTypesError && <Alert variant="danger">Error al cargar funcionalidades: {featureTypesError}</Alert>}

      {featureTypesLoading ? (
        <div className="d-flex justify-content-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover size="sm">
            <thead className="table-light">
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {featureTypes.map((ft) => (
                <tr key={ft.id}>
                  <td className="text-monospace">{ft.code}</td>
                  <td className="text-muted">{ft.description || '—'}</td>
                  <td>
                    <Badge bg={ft.isActive ? 'success' : 'secondary'}>
                      {ft.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant={ft.isActive ? 'outline-secondary' : 'outline-success'}
                      onClick={() => handleToggleFeatureActive(ft)}
                    >
                      {ft.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </td>
                </tr>
              ))}
              {featureTypes.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No hay funcionalidades registradas todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showNewFeatureModal} onHide={() => !creatingFeature && setShowNewFeatureModal(false)} centered>
        <Modal.Header closeButton={!creatingFeature}>
          <Modal.Title style={{ fontSize: '1rem' }}>Nueva funcionalidad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small">Código</Form.Label>
            <Form.Control
              size="sm"
              placeholder="ej. v2/decfiel"
              value={newFeatureCode}
              onChange={(e) => setNewFeatureCode(e.target.value)}
              disabled={creatingFeature}
            />
            <Form.Text className="text-muted">
              Letras, números, guion bajo, guion y diagonal (ej. <code>v2/decfiel</code>). No se puede cambiar
              después de creado — es el valor que usan los endpoints con <code>[RequireFeature]</code>.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small">Descripción</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={newFeatureDescription}
              onChange={(e) => setNewFeatureDescription(e.target.value)}
              disabled={creatingFeature}
            />
          </Form.Group>
          {createFeatureFeedback && (
            <Alert variant={createFeatureFeedback.ok ? 'success' : 'danger'} className="mb-0 small py-2">
              {createFeatureFeedback.message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowNewFeatureModal(false)} disabled={creatingFeature}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateFeature}
            disabled={creatingFeature || !newFeatureCode.trim()}
          >
            {creatingFeature ? <Spinner animation="border" size="sm" /> : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminFeatureConfig;
